import type { MissionProgressSnapshot } from '../../shared/types/mission-progress';
import type { MissionProgressRepository } from '../interfaces/repositories';
import { projectMissionProgress } from './governed-records';
import type { JsonStore } from './json-store';
import { STORAGE_KEYS } from './keys';
import { toMissionProgressDto } from './persistence-dto';
import { createWriteQueue, type WriteQueue } from './write-queue';

/**
 * The one in-flight mission snapshot (PRO-03).
 *
 * Exactly one: the shell offers "carry on where you left off", not a save-slot manager, and a
 * list of abandoned attempts is a privacy liability with no product behind it. Finishing a
 * mission clears the key rather than marking it complete.
 */
export function createLocalMissionProgressRepository(store: JsonStore): MissionProgressRepository {
  return {
    async getProgress(): Promise<MissionProgressSnapshot | null> {
      return store.read(STORAGE_KEYS.missionProgress, projectMissionProgress);
    },

    async saveProgress(snapshot: MissionProgressSnapshot): Promise<void> {
      const projected = toMissionProgressDto(snapshot);
      // A snapshot that cannot be rebuilt is not written at all: resuming from a half-record is
      // worse for a child than starting the mission again.
      if (projected === null) return;
      // Single-slot semantics (S1-5): a replacement that cannot reach the backend erases the
      // sitting it was superseding rather than leaving it to resurface after a reload.
      store.replaceSlot(STORAGE_KEYS.missionProgress, projected);
    },

    async clearProgress(): Promise<void> {
      // Verified removal: a snapshot that is only *tombstoned* is a mission that comes back.
      store.clearSlot(STORAGE_KEYS.missionProgress);
    },
  };
}

/**
 * One ordered chain for the one snapshot slot, shared by everything that writes to it (R2-1).
 *
 * The mission runtime serialises its own writes (D-052(1)), but that chain only knows about the
 * runtime: the shell clears the slot too — "Play again" after a sitting a grown-up ended early —
 * and that clear runs outside it. Ordering the repository *instance* is what makes the two the
 * same queue, because the shell and the runtime are handed the same instance.
 *
 * Since Slice R the queue is shared with the launch-identity repository as well, because the two
 * records describe one sitting and are cleared together (see `write-queue.ts`). Passing one in is
 * what makes that true; omitting it gives this repository a private chain, which is right for a
 * caller that has only this record.
 *
 * Reads are left un-chained on purpose: a read is not a mutation, and queueing "may I carry on?"
 * behind a write would make the shell's boot wait on it.
 */
export function withSerialisedWrites(
  inner: MissionProgressRepository,
  queue: WriteQueue = createWriteQueue(),
): MissionProgressRepository {
  const enqueue = queue;

  return {
    getProgress: () => inner.getProgress(),
    saveProgress: (snapshot) => enqueue(() => inner.saveProgress(snapshot)),
    clearProgress: () => enqueue(() => inner.clearProgress()),
  };
}
